// sim/landmarks.ts — named MAP LANDMARKS that make the world recognisable.
//
// A procedurally-noisy map reads as flat. Landmarks give it LEGIBLE, influential
// places: a Compost Mound is a rich renewable larder that draws rats; a Bird Nest is
// a persistent aerial-danger zone; a Rock Cluster walls off a chokepoint. Each type
// shapes its surroundings across the four axes the brief calls out:
//
//   • RESOURCES  — Compost/Garden/Branch drop renewable food nodes (regen-backed).
//   • WILDLIFE   — Bird/Frog/Spider/Rat seed + are kept replenished AT the landmark,
//                  so it becomes that species' territory (a real nest / pond / den).
//   • LOGISTICS  — Rock/Water fill an IMPASSABLE cluster: a chokepoint / barrier that
//                  foragers, couriers and pathing must route around.
//   • EXPANSION  — the terrain barriers block a colony's Scout/claim in that
//                  direction; the food hubs draw growth toward them. `expansionBiasAt`
//                  exposes the signed desirability for the AI / render.
//
// DETERMINISM: pure integer SoA, fixed order, its OWN RNG stream (so enabling
// landmarks never perturbs the colony/wildlife streams — it only ADDS features after
// the base world is laid out). Folds into the ecosystem hash. OPTIONAL — a world
// without `config.landmarks` is byte-identical.

import { KIND, LANDMARK_COUNT, NIL, SPECIES } from '../types.ts';
import type { System, Tick, Landmark, LandmarkConfig, Species } from '../types.ts';
import type { EntitySystem } from '../engine/EntitySystem.ts';
import type { WorldGrid } from '../engine/WorldGrid.ts';
import type { Rng } from '../rng.ts';
import type { ResourceSystem } from './resources.ts';
import type { ColonySystem } from './colony.ts';
import type { WildlifeSystem } from './wildlife.ts';

/** Per-type influence recipe (pure data, indexed by LANDMARK.*). */
interface LandmarkProfile {
  readonly name: string;
  readonly glyph: string;       // single-char ASCII map marker
  readonly foodNodes: number;   // renewable food nodes dropped in radius
  readonly foodAmount: number;  // amount per node
  readonly blocks: boolean;     // fill a small impassable cluster (Rock / Water)
  readonly species: number;     // wildlife species seeded + replenished (-1 = none)
  readonly wildlifeTarget: number; // target nearby population the respawn keeps alive
  readonly expansionBias: number;  // signed desirability for colony expansion
}

const NONE = -1;
const LANDMARK_PROFILE: readonly LandmarkProfile[] = [
  // FallenBranch — a little extra forage + light cover.
  { name: 'Fallen Branch', glyph: 'Y', foodNodes: 2, foodAmount: 16, blocks: false, species: NONE, wildlifeTarget: 0, expansionBias: 1 },
  // CompostMound — RICH renewable larder; draws Rats (scavenger raiders).
  { name: 'Compost Mound', glyph: '@', foodNodes: 4, foodAmount: 24, blocks: false, species: SPECIES.Rat, wildlifeTarget: 2, expansionBias: 2 },
  // BirdNest — persistent aerial-danger territory.
  { name: 'Bird Nest', glyph: 'V', foodNodes: 0, foodAmount: 0, blocks: false, species: SPECIES.Bird, wildlifeTarget: 2, expansionBias: -3 },
  // GardenBed — RICH plant food; draws a herbivore Beetle.
  { name: 'Garden Bed', glyph: '*', foodNodes: 4, foodAmount: 22, blocks: false, species: SPECIES.Beetle, wildlifeTarget: 1, expansionBias: 2 },
  // RockCluster — impassable chokepoint sheltering a Spider.
  { name: 'Rock Cluster', glyph: '#', foodNodes: 0, foodAmount: 0, blocks: true, species: SPECIES.Spider, wildlifeTarget: 1, expansionBias: -2 },
  // WaterPuddle — impassable barrier ringed by a Frog's territory.
  { name: 'Water Puddle', glyph: '~', foodNodes: 0, foodAmount: 0, blocks: true, species: SPECIES.Frog, wildlifeTarget: 1, expansionBias: -2 },
];

/**
 * Effective landmark count for a map of `width*height` tiles. DETERMINISTIC + pure
 * (no RNG): a function only of the config + map size, so the system and the entity
 * capacity reservation agree on one number.
 *
 *   • `densityPer` UNSET ⇒ exactly `count` (legacy / byte-identical placement).
 *   • `densityPer` SET   ⇒ round(area / densityPer), clamped to [count, maxCount].
 *     `count` is the FLOOR (a tiny map still gets the baseline handful) and
 *     `maxCount` (default 24) the ceiling, so the layer scales with area without
 *     ever exploding on a huge world.
 */
export function landmarkTargetCount(config: LandmarkConfig, width: number, height: number): number {
  const base = Math.max(0, Math.trunc(config.count));
  const densityPer = Math.trunc(config.densityPer ?? 0);
  if (densityPer <= 0) return base; // legacy: exactly `count`
  const area = Math.max(0, Math.trunc(width)) * Math.max(0, Math.trunc(height));
  const maxCount = Math.max(base, Math.trunc(config.maxCount ?? 24));
  const scaled = Math.round(area / densityPer);
  return scaled < base ? base : scaled > maxCount ? maxCount : scaled;
}

/** Public, pure accessor — the type's display name. */
export function landmarkName(type: number): string {
  return (LANDMARK_PROFILE[type] as LandmarkProfile | undefined)?.name ?? '?';
}
/** Public, pure accessor — the type's ASCII map glyph. */
export function landmarkGlyph(type: number): string {
  return (LANDMARK_PROFILE[type] as LandmarkProfile | undefined)?.glyph ?? '?';
}

export class LandmarkSystem implements System {
  readonly name = 'landmarks';

  private readonly entities: EntitySystem;
  private readonly grid: WorldGrid;
  private readonly resources: ResourceSystem;
  private readonly colonies: ColonySystem;
  private readonly wildlife: WildlifeSystem;
  private readonly rng: Rng;
  private readonly config: LandmarkConfig;

  // --- Per-landmark SoA. ---
  private readonly type: Uint8Array;
  private readonly lx: Int32Array;
  private readonly ly: Int32Array;
  private n = 0;
  /** Area-scaled target count (resolved once at construction; the SoA capacity). */
  private readonly targetCount: number;

  // --- Public counters (deterministic; folded into the hash, read by tests). ---
  wildlifeSpawnedTotal = 0;
  respawnPasses = 0;

  constructor(
    entities: EntitySystem,
    grid: WorldGrid,
    resources: ResourceSystem,
    colonies: ColonySystem,
    wildlife: WildlifeSystem,
    rng: Rng,
    config: LandmarkConfig,
  ) {
    this.entities = entities;
    this.grid = grid;
    this.resources = resources;
    this.colonies = colonies;
    this.wildlife = wildlife;
    this.rng = rng;
    this.config = config;
    // Area-scaled target (≡ config.count when densityPer is unset). Drives both the
    // generation loop and the SoA capacity, so they never disagree.
    this.targetCount = landmarkTargetCount(config, grid.width, grid.height);
    const cap = Math.max(1, this.targetCount);
    this.type = new Uint8Array(cap);
    this.lx = new Int32Array(cap);
    this.ly = new Int32Array(cap);
  }

  // --- Generation (once, at world build) -------------------------------------

  /**
   * Place the landmarks + apply their one-time influence (terrain, food, initial
   * wildlife). Call AFTER the base world (walls/food/colonies/wildlife) is laid out,
   * so landmarks AUGMENT it. Deterministic via the landmark RNG; never touches the
   * colony or wildlife RNG streams.
   */
  generate(): void {
    const cfg = this.config;
    const r = cfg.radius;
    const guardMax = this.grid.size * 4;
    // OPT-IN: keep FOOD landmarks (renewable larders) at least `foodMinNestDist`
    // (Chebyshev) from any nest, so a larder never lands so close that foragers' trips
    // are too short for a Primary/Major supply route to harden (see logistics.test seed
    // note). 0 (the default) ⇒ only the existing clear-of-nest check ⇒ byte-identical
    // (no extra RNG rejections, so the placement stream is unchanged). Field lives off
    // the shared LandmarkConfig type (widened here, like timescale/projects elsewhere).
    const foodMinNestDist = Math.max(
      0,
      Math.trunc((cfg as LandmarkConfig & { readonly foodMinNestDist?: number }).foodMinNestDist ?? 0),
    );
    // VARIED TYPE MIX (was a deterministic `i % LANDMARK_COUNT` cycle): build the
    // per-slot type list, then shuffle it. VARIETY GUARANTEE preserved — the first
    // min(target, LANDMARK_COUNT) slots take one of EACH type (so a default-sized
    // map still shows the full set); any surplus slots on a larger world are filled
    // by a uniform landmark-RNG pick (LANDMARK_PROFILE carries no natural weight, so
    // the mix is uniform). The list is then Fisher–Yates shuffled with the SAME
    // landmark RNG so placement order varies too. All draws are integer + from the
    // isolated landmark stream ⇒ same seed+config ⇒ identical type+position set.
    const total = this.targetCount;
    const types = new Array<number>(total);
    for (let i = 0; i < total; i++) {
      types[i] = i < LANDMARK_COUNT ? i : this.rng.int(LANDMARK_COUNT);
    }
    for (let i = total - 1; i > 0; i--) {
      const j = this.rng.int(i + 1);
      const tmp = types[i] as number;
      types[i] = types[j] as number;
      types[j] = tmp;
    }
    for (let i = 0; i < total; i++) {
      const type = (types[i] as number) as Landmark;
      // FOOD landmarks (renewable larders) carry a stricter nest-clearance when configured.
      const isFood = ((LANDMARK_PROFILE[type] as LandmarkProfile).foodNodes) > 0;
      // Find a centre: passable, clear of nests + other landmarks.
      let cx = -1;
      let cy = -1;
      let guard = 0;
      while (guard++ < guardMax) {
        const x = this.rng.int(this.grid.width);
        const y = this.rng.int(this.grid.height);
        if (!this.grid.isPassable(x, y)) continue;
        if (this.tooClose(x, y, r + 2)) continue;
        // A FOOD larder must additionally sit beyond foodMinNestDist of every nest.
        if (foodMinNestDist > 0 && isFood && this.nearNest(x, y, foodMinNestDist)) continue;
        cx = x; cy = y; break;
      }
      if (cx < 0) continue; // couldn't place — skip this landmark
      const slot = this.n++;
      this.type[slot] = type;
      this.lx[slot] = cx;
      this.ly[slot] = cy;
      this.applyInfluence(slot);
    }
  }

  /** True if (x,y) is within `d` (Chebyshev) of any colony nest or placed landmark. */
  private tooClose(x: number, y: number, d: number): boolean {
    const c = this.colonies;
    for (let cid = 0; cid < c.count; cid++) {
      if (cheby(x, y, c.homeXAt(cid), c.homeYAt(cid)) <= d) return true;
    }
    for (let k = 0; k < this.n; k++) {
      if (cheby(x, y, this.lx[k] as number, this.ly[k] as number) <= d) return true;
    }
    return false;
  }

  /** One-time terrain / food / wildlife influence for landmark `slot`. */
  private applyInfluence(slot: number): void {
    const p = LANDMARK_PROFILE[this.type[slot] as number] as LandmarkProfile;
    const cx = this.lx[slot] as number;
    const cy = this.ly[slot] as number;
    const r = this.config.radius;

    // LOGISTICS: fill a small impassable cluster (Rock / Water), skipping protected
    // tiles (nests, food, the world edge) so we never strand the base world.
    if (p.blocks) {
      const br = r >= 2 ? 2 : 1;
      for (let dy = -br; dy <= br; dy++) {
        for (let dx = -br; dx <= br; dx++) {
          if (dx * dx + dy * dy > br * br + 1) continue; // rough disc
          const x = cx + dx;
          const y = cy + dy;
          if (!this.grid.inBounds(x, y)) continue;
          if (!this.grid.isPassable(x, y)) continue;       // already wall
          if (this.tileBlockedHere(x, y)) continue;        // a food node / occupied tile
          if (this.nearNest(x, y, 1)) continue;            // never wall in a nest
          this.grid.setPassable(x, y, false);
        }
      }
    }

    // RESOURCES: drop renewable food nodes within radius (regen keeps them topped).
    for (let f = 0; f < p.foodNodes; f++) {
      const t = this.passableNear(cx, cy, r);
      if (t < 0) break;
      this.resources.addFood(t % this.grid.width, Math.floor(t / this.grid.width), p.foodAmount);
    }

    // WILDLIFE: seed the territory's initial population at the landmark.
    if (p.species !== NONE) {
      for (let w = 0; w < p.wildlifeTarget; w++) this.spawnAt(cx, cy, r, p.species as number);
    }
  }

  // --- Per-tick: keep each wildlife landmark's territory alive -----------------

  update(tick: Tick): void {
    if (this.n === 0) return;
    if (tick % this.config.respawnInterval !== 0) return;
    const r = this.config.radius;
    for (let slot = 0; slot < this.n; slot++) {
      const p = LANDMARK_PROFILE[this.type[slot] as number] as LandmarkProfile;
      if (p.species === NONE || p.wildlifeTarget === 0) continue;
      const cx = this.lx[slot] as number;
      const cy = this.ly[slot] as number;
      const have = this.countSpeciesNear(cx, cy, r + 1, p.species as number);
      // Top up by ONE per pass (a gentle drip, never a swarm) toward the target.
      if (have < p.wildlifeTarget) this.spawnAt(cx, cy, r, p.species as number);
    }
    this.respawnPasses++;
  }

  // --- Influence query (the EXPANSION + render seam) --------------------------

  /**
   * Signed expansion desirability at (x,y): SUM of nearby landmark biases (food hubs
   * positive, danger/barriers negative), each falling off with distance. The colony
   * AI / render can read this to favour fertile ground and shun nests/rocks.
   */
  expansionBiasAt(x: number, y: number): number {
    const r = this.config.radius;
    let bias = 0;
    for (let k = 0; k < this.n; k++) {
      const d = cheby(x, y, this.lx[k] as number, this.ly[k] as number);
      if (d > r) continue;
      const p = LANDMARK_PROFILE[this.type[k] as number] as LandmarkProfile;
      bias += p.expansionBias * (r - d + 1);
    }
    return bias;
  }

  // --- Accessors (hash / CLI / render / tests) --------------------------------

  count(): number { return this.n; }
  /** The area-scaled count we attempted to place (SoA capacity; ≥ count() if any were skipped). */
  target(): number { return this.targetCount; }
  typeAt(i: number): number { return this.type[i] as number; }
  xAt(i: number): number { return this.lx[i] as number; }
  yAt(i: number): number { return this.ly[i] as number; }
  radius(): number { return this.config.radius; }

  /** Deterministic fold of the landmark state into the world hash. */
  hash(h: number, mix: (h: number, v: number) => number): number {
    h = mix(h, this.n);
    for (let i = 0; i < this.n; i++) {
      h = mix(h, this.type[i] as number);
      h = mix(h, this.lx[i] as number);
      h = mix(h, this.ly[i] as number);
    }
    h = mix(h, this.wildlifeSpawnedTotal);
    h = mix(h, this.respawnPasses);
    return h;
  }

  // --- helpers ---------------------------------------------------------------

  /** Spawn one `species` wildlife on a passable tile within `r` of (cx,cy). */
  private spawnAt(cx: number, cy: number, r: number, species: number): void {
    const t = this.passableNear(cx, cy, r);
    if (t < 0) return;
    this.wildlife.spawnWildlife(species as Species, t % this.grid.width, Math.floor(t / this.grid.width));
    this.wildlifeSpawnedTotal++;
  }

  /** A passable tile index within `r` of (cx,cy), nearest-first deterministic scan, or -1. */
  private passableNear(cx: number, cy: number, r: number): number {
    for (let ring = 0; ring <= r; ring++) {
      for (let dy = -ring; dy <= ring; dy++) {
        for (let dx = -ring; dx <= ring; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== ring) continue; // ring perimeter only
          const x = cx + dx;
          const y = cy + dy;
          if (!this.grid.inBounds(x, y)) continue;
          if (!this.grid.isPassable(x, y)) continue;
          if (this.tileBlockedHere(x, y)) continue; // don't stack on a food node
          return y * this.grid.width + x;
        }
      }
    }
    return -1;
  }

  /** True if a non-passable-relevant entity (food) already sits on (x,y). */
  private tileBlockedHere(x: number, y: number): boolean {
    const e = this.entities;
    const ti = this.grid.index(x, y);
    for (let cur = this.grid.headAt(ti); cur !== NIL; cur = this.grid.next(cur)) {
      if ((e.kind[cur as number] as number) === KIND.Food) return true;
    }
    return false;
  }

  private nearNest(x: number, y: number, d: number): boolean {
    const c = this.colonies;
    for (let cid = 0; cid < c.count; cid++) {
      if (cheby(x, y, c.homeXAt(cid), c.homeYAt(cid)) <= d) return true;
    }
    return false;
  }

  private countSpeciesNear(cx: number, cy: number, r: number, species: number): number {
    const e = this.entities;
    const grid = this.grid;
    const wl = this.wildlife;
    let count = 0;
    const minX = cx - r < 0 ? 0 : cx - r;
    const minY = cy - r < 0 ? 0 : cy - r;
    const maxX = cx + r >= grid.width ? grid.width - 1 : cx + r;
    const maxY = cy + r >= grid.height ? grid.height - 1 : cy + r;
    for (let ty = minY; ty <= maxY; ty++) {
      for (let tx = minX; tx <= maxX; tx++) {
        const ti = grid.index(tx, ty);
        for (let cur = grid.headAt(ti); cur !== NIL; cur = grid.next(cur)) {
          const id = cur as number;
          if ((e.kind[id] as number) !== KIND.Wildlife) continue;
          if ((e.hp[id] as number) <= 0) continue;
          if ((wl.species[id] as number) === species) count++;
        }
      }
    }
    return count;
  }
}

function cheby(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx < 0 ? bx - ax : ax - bx;
  const dy = ay - by < 0 ? by - ay : ay - by;
  return dx > dy ? dx : dy;
}
